import React, { useState } from 'react';
import type { Clase, NivelClase } from '../../types';
import { ALTURAS, AUTONOMIAS, DURACIONES, NIVELES_CLASE, OBJETIVOS, POBLACIONES, TIPOS_CLASE } from '../../data/constantes';
import { NIVELES_INFO, PLANTILLAS } from '../../data/niveles';
import { CampoArea, CampoNumero, CampoSelect, CampoTexto } from '../ui/Campo';
import { Tarjeta } from '../ui/Tarjeta';
import { Aviso } from '../ui/Aviso';
import { Boton } from '../ui/Boton';
export type CreationMode = null | 'blank' | 'template';
interface Props {
  clase: Clase;
  actualizar: (parcial: Partial<Clase>) => void;
  errores: Record<string, string>;
  creationMode: CreationMode;
  onCreationModeChange: (mode: Exclude<CreationMode, null>) => void;
}
export function Paso1Grupo({
  clase,
  actualizar,
  errores,
  creationMode,
  onCreationModeChange
}: Props) {
  const [duracionPersonalizada, setDuracionPersonalizada] = useState(!DURACIONES.includes(clase.duracion));
  const info = NIVELES_INFO[clase.nivel];
  const plantilla = PLANTILLAS[info.plantillaId];
  return <div className="space-y-4" data-id="element-67" data-mp="2hhejvozf5687">
      <Tarjeta data-id="element-68" data-mp="2meo6yxzc55sv">
        <h2 className="text-lg font-bold text-tinta-titulo" data-id="element-69" data-mp="2mf69udgi6i9r">Conocé el punto de partida</h2>
        <p className="mt-1 text-sm text-tinta-sec" data-id="element-70" data-mp="28f5bp5rmhoxz">
          Estos datos ordenan el resto del asistente y las sugerencias de la biblioteca.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-71" data-mp="2ci1ogfuhequ0">
          <CampoTexto etiqueta="Título de la clase" requerido value={clase.titulo} error={errores.titulo} placeholder="Por ejemplo: Martes 19 h — llaves de pie" onChange={e => actualizar({
          titulo: e.target.value
        })} data-id="element-72" data-mp="30631a0yp0tf8" />
          <CampoTexto etiqueta="Fecha" type="date" value={clase.fecha} ayuda="Se muestra como dd/mm/aaaa en la clase y en la impresión." onChange={e => actualizar({
          fecha: e.target.value
        })} data-id="element-73" data-mp="2vd9vd9rhnf1k" />
          <CampoTexto etiqueta="Grupo o espacio (opcional)" value={clase.grupo} onChange={e => actualizar({
          grupo: e.target.value
        })} data-id="element-74" data-mp="1m89twbbz230" />
          <CampoSelect etiqueta="Población" value={clase.poblacion} opciones={POBLACIONES} onChange={e => actualizar({
          poblacion: e.target.value as Clase['poblacion']
        })} data-id="element-75" data-mp="3oq64d1trs0wi" />
          <CampoSelect etiqueta="Nivel" value={clase.nivel} opciones={NIVELES_CLASE} onChange={e => actualizar({
          nivel: e.target.value as NivelClase
        })} data-id="element-76" data-mp="3eta0xaiqa4yt" />
          <CampoSelect etiqueta="Autonomía" value={clase.autonomia} opciones={AUTONOMIAS} onChange={e => actualizar({
          autonomia: e.target.value as Clase['autonomia']
        })} data-id="element-77" data-mp="360g80aacz72h" />
          <CampoNumero etiqueta="Participantes" min={1} max={40} value={clase.participantes} error={errores.participantes} onChange={e => actualizar({
          participantes: Number(e.target.value)
        })} data-id="element-78" data-mp="2w3lbty1k5ird" />
          <CampoNumero etiqueta="Telas o puntos disponibles" min={1} max={20} value={clase.puntos} error={errores.puntos} onChange={e => actualizar({
          puntos: Number(e.target.value)
        })} data-id="element-79" data-mp="2lvzadzenrhwl" />
          <div data-id="element-80" data-mp="1y9apd4ucr9br">
            <CampoSelect etiqueta="Duración" value={duracionPersonalizada ? 'Personalizada' : String(clase.duracion)} opciones={[...DURACIONES.map(d => String(d)), 'Personalizada']} onChange={e => {
            if (e.target.value === 'Personalizada') {
              setDuracionPersonalizada(true);
            } else {
              setDuracionPersonalizada(false);
              actualizar({
                duracion: Number(e.target.value)
              });
            }
          }} data-id="element-81" data-mp="1y9ss8kbislsn" />
            {duracionPersonalizada && <div className="mt-2" data-id="element-82" data-mp="3m6jq3knn1pcg">
                <CampoNumero etiqueta="Duración personalizada (30 a 180 min)" min={30} max={180} value={clase.duracion} error={errores.duracion} onChange={e => actualizar({
              duracion: Number(e.target.value)
            })} data-id="element-83" data-mp="3h6xhxpmo2zai" />
              </div>}
          </div>
          <CampoSelect etiqueta="Tipo de clase" value={clase.tipo} opciones={TIPOS_CLASE} onChange={e => actualizar({
          tipo: e.target.value as Clase['tipo']
        })} data-id="element-84" data-mp="8sykejk1r5uw" />
          <CampoSelect etiqueta="Altura de trabajo" value={clase.altura} opciones={ALTURAS} onChange={e => actualizar({
          altura: e.target.value as Clase['altura']
        })} data-id="element-85" data-mp="dqgljnrtogsc" />
          <CampoSelect etiqueta="Objetivo" value={clase.objetivo} opciones={OBJETIVOS} onChange={e => actualizar({
          objetivo: e.target.value
        })} data-id="element-86" data-mp="2filixes3c6ag" />
          {clase.objetivo === 'Personalizado' && <CampoTexto etiqueta="Objetivo personalizado" value={clase.objetivoPersonalizado} onChange={e => actualizar({
          objetivoPersonalizado: e.target.value
        })} data-id="element-87" data-mp="l9pslacuw3e8" />}
        </div>

        <div className="mt-4" data-id="element-88" data-mp="c88hy7i7wtwx">
          <CampoArea etiqueta="Consideraciones del grupo (opcional)" value={clase.consideraciones} ayuda="Anotá adaptaciones y necesidades organizativas. Evitá guardar diagnósticos médicos detallados o datos sensibles." onChange={e => actualizar({
          consideraciones: e.target.value
        })} data-id="element-89" data-mp="cb4hg09prqnh" />
        </div>

        {clase.participantes > clase.puntos && <Aviso tipo="aviso" className="mt-4" titulo="Revisá la organización" data-id="element-90" data-mp="1wn2487euxqay">
            Hay más participantes que puntos de trabajo. Sumá una estrategia de rotación o
            estaciones.
          </Aviso>}

        {clase.nivel === 'Introducción' && (clase.altura === 'Altura' || clase.altura === 'Mixta') && <Aviso tipo="aviso" className="mt-3" titulo="Altura en Introducción" data-id="element-91" data-mp="1q83834c9hfd8">
            En Introducción conviene priorizar Piso, Nudo y Baja altura.
          </Aviso>}
      </Tarjeta>

      <Tarjeta className="border-vino/20 bg-vino/5" data-id="element-92" data-mp="2ixtmleht2j9v">
        <h2 className="text-lg font-bold text-vino" data-id="element-93" data-mp="2iwio9u74q3q5">Nivel: {info.nivel}</h2>
        <p className="mt-1 text-sm text-tinta-sec" data-id="element-94" data-mp="2dzidj5zc4csp">{info.publico}</p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-95" data-mp="296yys5owvbmb">
          <div className="rounded-xl border border-vino/20 bg-white p-3" data-id="element-96" data-mp="2954fswx79tkv">
            <h3 className="text-xs font-bold uppercase tracking-wide text-tinta-tenue" data-id="element-97" data-mp="255n8dxy7qmd2">
              Objetivos orientativos
            </h3>
            <ul className="mt-2 list-inside list-disc text-sm text-tinta" data-id="element-98" data-mp="2wxbh8ahs8qr5">
              {info.objetivos.map(o => <li key={o} data-id="element-99" data-mp="jf6zk60dl8s3">{o}</li>)}
            </ul>
          </div>
          <div className="space-y-3" data-id="element-100" data-mp="od06j19i66tf">
            <div className="rounded-xl border border-vino/20 bg-white p-3" data-id="element-101" data-mp="oe1uu7fj4pt3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-tinta-tenue" data-id="element-102" data-mp="se28vqypuyzm">
                Criterio
              </h3>
              <p className="mt-1 text-sm text-tinta" data-id="element-103" data-mp="xfhwnqsa3jlp">{info.prioridad}</p>
            </div>
            <div className="rounded-xl border border-vino/20 bg-white p-3" data-id="element-104" data-mp="12crqdimpahfc">
              <h3 className="text-xs font-bold uppercase tracking-wide text-tinta-tenue" data-id="element-105" data-mp="17bcj6wzmhytr">
                No se sugiere automáticamente
              </h3>
              <p className="mt-1 text-sm text-tinta" data-id="element-106" data-mp="1g4ohbjcmula5">{info.noAutomatico}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-vino/20 bg-white p-3" data-id="element-107" data-mp="1l4au5rojtzif">
          <h3 className="text-sm font-bold text-tinta-titulo" data-id="element-108" data-mp="1q2vk8ya372t5">Punto de partida de la estructura</h3>
          <p className="mt-1 text-sm text-tinta-sec" data-id="element-109" data-mp="10zq8837yh536">
            Plantilla sugerida: {plantilla.nombre} ({plantilla.duracion} min). Las plantillas crean
            la estructura de bloques; los recursos técnicos los elegís vos.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-110" data-mp="19zd7ojh4k0tl">
            <div data-id="element-111" data-mp="15ytbpj8af391">
              <Boton variante={creationMode === 'blank' ? 'secundario' : 'contorno'} className={`w-full ${creationMode === 'blank' ? 'border-vino bg-vino text-white hover:bg-vino-claro' : 'border-vino/20 bg-white text-vino'}`} aria-pressed={creationMode === 'blank'} onClick={() => onCreationModeChange('blank')} data-id="element-112" data-mp="15y9slz6wi8tf">
                Empezar en blanco
              </Boton>
              <p className="mt-2 text-xs leading-5 text-tinta-sec" data-id="element-113" data-mp="3upb0ystlmzzl">
                Creá tus propios bloques y distribuí los minutos desde cero.
              </p>
            </div>
            <div data-id="element-114" data-mp="3gktvdinkll9f">
              <Boton variante={creationMode === 'template' ? 'secundario' : 'contorno'} className={`w-full ${creationMode === 'template' ? 'border-vino bg-vino text-white hover:bg-vino-claro' : 'border-vino/20 bg-white text-vino'}`} aria-pressed={creationMode === 'template'} onClick={() => onCreationModeChange('template')} data-id="element-115" data-mp="3gkbuu9tvkkvp">
                Usar plantilla del nivel
              </Boton>
              <p className="mt-2 text-xs leading-5 text-tinta-sec" data-id="element-116" data-mp="3pdfex0z0zvzb">
                Cargá una estructura sugerida con bloques y tiempos. Después elegís los ejercicios.
              </p>
            </div>
          </div>
        </div>

        {clase.nivel === 'Multinivel' && <Aviso tipo="info" className="mt-3" titulo="Multinivel es una modalidad" data-id="element-117" data-mp="2dwxj93395xdq">
            El objetivo principal necesita Regresión, Base y Progresión, además de una estrategia de
            puntos con rotaciones o estaciones y una nota sobre qué grupo necesita observación
            directa.
          </Aviso>}
      </Tarjeta>
    </div>;
}